// Generacion procedural del cielo del login (sin React, sin DOM).
// La nebulosa y la via lactea se calculan una sola vez en baja resolucion;
// el componente la escala y le pinta encima el polvo estelar a resolucion real.

export const ANCHO_NEBULOSA = 400;
export const ALTO_NEBULOSA = 225;
const ASPECTO = ANCHO_NEBULOSA / ALTO_NEBULOSA;
// Direccion de la banda (de abajo-izquierda a arriba-derecha), normalizada
const EJE_X = ASPECTO / Math.hypot(ASPECTO, 0.82);
const EJE_Y = -0.82 / Math.hypot(ASPECTO, 0.82);

// PRNG deterministico (mulberry32): el cielo es siempre el mismo
export const crearRng = (semilla) => () => {
  semilla = (semilla + 0x6d2b79f5) | 0;
  let t = Math.imul(semilla ^ (semilla >>> 15), 1 | semilla);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

// Value noise 2D + fbm
const crearRuido = (rng) => {
  const p = Array.from({ length: 256 }, (_, i) => i);
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [p[i], p[j]] = [p[j], p[i]];
  }
  const perm = new Uint8Array(512);
  for (let i = 0; i < 512; i++) perm[i] = p[i & 255];
  const valores = Float32Array.from({ length: 256 }, () => rng());
  const suave = (t) => t * t * (3 - 2 * t);

  const ruido = (x, y) => {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const u = suave(x - xi);
    const v = suave(y - yi);
    const X = xi & 255;
    const Y = yi & 255;
    const a = valores[perm[X + perm[Y]]];
    const b = valores[perm[X + 1 + perm[Y]]];
    const c = valores[perm[X + perm[Y + 1]]];
    const d = valores[perm[X + 1 + perm[Y + 1]]];
    const arriba = a + (b - a) * u;
    return arriba + (c + (d - c) * u - arriba) * v;
  };

  return (x, y, octavas = 5) => {
    let suma = 0;
    let amp = 0.5;
    let freq = 1;
    let norma = 0;
    for (let o = 0; o < octavas; o++) {
      suma += ruido(x * freq, y * freq) * amp;
      norma += amp;
      amp *= 0.5;
      freq *= 2.03;
    }
    return suma / norma;
  };
};

const paso = (a, b, x) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

// Banda de la via lactea: diagonal de abajo-izquierda a arriba-derecha,
// con una curva suave para que no parezca trazada con regla.
// u, v en [0, 1] (coordenadas de la nebulosa). Devuelve [0, 1].
export const banda = (u, v) => {
  const X = u * ASPECTO;
  const dx = ASPECTO;
  const dy = 0.12 - 0.94;
  const largo = Math.hypot(dx, dy);
  const dist = ((X - 0) * dy - (v - 0.94) * dx) / largo + 0.05 * Math.sin(X * 2.3 + 0.6);
  const sigma = 0.15;
  return Math.exp(-(dist * dist) / (2 * sigma * sigma));
};

export const generarNebulosa = (semilla = 7) => {
  const rng = crearRng(semilla);
  const fbm = crearRuido(rng);
  const W = ANCHO_NEBULOSA;
  const H = ALTO_NEBULOSA;
  const datos = new Uint8ClampedArray(W * H * 4);
  // Opacidad de las franjas de polvo: el polvo estelar la usa para no
  // dibujar estrellas encima de las zonas oscuras
  const polvoMapa = new Float32Array(W * H);

  for (let y = 0; y < H; y++) {
    const v = y / (H - 1);
    for (let x = 0; x < W; x++) {
      const u = x / (W - 1);
      const X = u * ASPECTO;
      const Y = v;

      // Domain warping: gas filamentoso en vez de manchas redondas
      const qx = fbm(X * 1.5, Y * 1.5, 4);
      const qy = fbm(X * 1.5 + 5.2, Y * 1.5 + 1.3, 4);
      const n = fbm(X * 2.4 + qx * 2.6, Y * 2.4 + qy * 2.6, 5);
      const nubes = paso(0.3, 0.78, n);
      const cresta = 1 - Math.abs(2 * fbm(X * 3.2 + qy * 3, Y * 3.2 + qx * 3, 5) - 1);
      const filamentos = Math.pow(cresta, 9);
      const b = banda(u, v);
      // Franjas de polvo estiradas a lo largo de la banda
      const largo = X * EJE_X + Y * EJE_Y;
      const ancho = -X * EJE_Y + Y * EJE_X;
      const polvo =
        paso(0.5, 0.63, fbm(largo * 3.2 + qx * 1.4 + 11, ancho * 9 + qy * 1.4 + 4, 5)) * paso(0.15, 0.7, b);
      const tono = paso(0.34, 0.66, fbm(X * 1.1 + 20, Y * 1.1 + 7, 3));

      const gas =
        b * (0.18 + 0.82 * nubes) * 1.15 * (1 - 0.88 * polvo) +
        nubes * 0.07 * (1 - b) +
        filamentos * (0.04 + 0.16 * b) * (1 - 0.7 * polvo);

      // Fondo: azul noche casi negro, un poco mas claro abajo (luz del planeta)
      const cx = u - 0.5;
      const cy = v - 0.5;
      const vineta = 1 - 0.5 * (cx * cx * 1.6 + cy * cy * 2.2);
      let r = (0.004 + 0.012 * v * v) * vineta;
      let g = (0.012 + 0.036 * v * v) * vineta;
      let a = (0.04 + 0.1 * v * v) * vineta;

      // Gas: cobalto, que vira a cian en las zonas densas y a violeta en las frias
      const brillo = paso(0.35, 1.0, gas);
      const frio = (1 - tono) * 0.55;
      const gr = 0.08 + 0.16 * brillo + 0.34 * frio;
      const gg = 0.22 + 0.42 * brillo + 0.04 * frio;
      const gb = 0.78 + 0.2 * brillo + 0.1 * frio;
      r += gas * gr * 0.7;
      g += gas * gg * 0.7;
      a += gas * gb * 0.7;

      // Nucleo de la banda, casi blanco
      const nucleo = Math.pow(b, 6) * (1 - polvo) * nubes * 0.22;
      r += nucleo * 0.8;
      g += nucleo * 0.9;
      a += nucleo;

      const i = y * W + x;
      polvoMapa[i] = polvo;
      datos[i * 4] = 255 * (1 - Math.exp(-r * 1.6));
      datos[i * 4 + 1] = 255 * (1 - Math.exp(-g * 1.6));
      datos[i * 4 + 2] = 255 * (1 - Math.exp(-a * 1.6));
      datos[i * 4 + 3] = 255;
    }
  }
  return { datos, polvoMapa };
};

// Polvo estelar fijo, concentrado en la banda y escondido tras las franjas
// oscuras. Coordenadas en [0, 1].
export const generarPolvo = (cantidad, polvoMapa, semilla = 11) => {
  const rng = crearRng(semilla);
  const puntos = [];
  let intentos = 0;
  while (puntos.length < cantidad && intentos < cantidad * 30) {
    intentos++;
    const u = rng();
    const v = rng();
    const b = banda(u, v);
    if (rng() > 0.08 + 0.92 * Math.pow(b, 1.4)) continue;
    const px = Math.min(ANCHO_NEBULOSA - 1, Math.round(u * (ANCHO_NEBULOSA - 1)));
    const py = Math.min(ALTO_NEBULOSA - 1, Math.round(v * (ALTO_NEBULOSA - 1)));
    const oculto = polvoMapa[py * ANCHO_NEBULOSA + px];
    if (rng() < oculto * 0.9) continue;
    puntos.push({
      u,
      v,
      r: 0.3 + Math.pow(rng(), 3.5) * 0.8,
      a: (0.1 + Math.pow(rng(), 2.4) * 0.75) * (1 - oculto * 0.6),
      tinte: rng() < 0.72 ? 0 : rng() < 0.7 ? 1 : 2,
    });
  }
  return puntos;
};
